import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { TOUR_STEPS, TourStep } from '../components/tour/tourSteps';
import { useSaaSContext } from './SaaSContext';
import { useThreeDContext } from './ThreeDContext';
import { useToast } from './ToastContext';
import { VdpElement } from '../types';

interface TourContextType {
  isTourOpen: boolean;
  currentStepIndex: number;
  totalSteps: number;
  currentStep: TourStep;
  startTour: (initialIndex?: number) => void;
  closeTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
  showWelcomePrompt: boolean;
  dismissWelcomePrompt: () => void;
  hasCompletedTour: boolean;
  runStepAction: () => void;
  hasAction: boolean;
}

const LOCAL_STORAGE_TOUR_COMPLETED = 'vdp_tour_completed_v1';
const LOCAL_STORAGE_WELCOME_DISMISSED = 'vdp_tour_welcome_dismissed_v1';

const TourContext = createContext<TourContextType | undefined>(undefined);

export const TourProvider = ({ children }: { children?: ReactNode }) => {
  const { 
    activeStep, 
    setActiveStep, 
    setCsvData, 
    template, 
    setTemplate, 
    layout, 
    setLayout 
  } = useSaaSContext();
  const threeD = useThreeDContext();
  const { toast } = useToast();

  const [isTourOpen, setIsTourOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(() => {
    try {
      return localStorage.getItem(LOCAL_STORAGE_TOUR_COMPLETED) === 'true';
    } catch {
      return false;
    }
  });

  const [showWelcomePrompt, setShowWelcomePrompt] = useState(() => {
    try {
      const completed = localStorage.getItem(LOCAL_STORAGE_TOUR_COMPLETED) === 'true';
      const dismissed = localStorage.getItem(LOCAL_STORAGE_WELCOME_DISMISSED) === 'true';
      return !completed && !dismissed;
    } catch {
      return false;
    }
  });

  const currentStep = TOUR_STEPS[currentStepIndex] || TOUR_STEPS[0];

  const syncTabForStep = useCallback((step: TourStep) => {
    if (step.tab && step.tab !== activeStep) {
      setActiveStep(step.tab);
    }
  }, [activeStep, setActiveStep]);

  const startTour = useCallback((initialIndex = 0) => {
    const validIndex = Math.max(0, Math.min(initialIndex, TOUR_STEPS.length - 1));
    setCurrentStepIndex(validIndex);
    setIsTourOpen(true);
    setShowWelcomePrompt(false);
    syncTabForStep(TOUR_STEPS[validIndex]);
  }, [syncTabForStep]);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStepIndex < TOUR_STEPS.length - 1) {
      const nextIdx = currentStepIndex + 1;
      setCurrentStepIndex(nextIdx);
      syncTabForStep(TOUR_STEPS[nextIdx]);
    } else {
      // Completed last step
      setIsTourOpen(false);
      setHasCompletedTour(true);
      try {
        localStorage.setItem(LOCAL_STORAGE_TOUR_COMPLETED, 'true');
      } catch {}
      toast.success('Tour Completed', '🎉 You are now ready to design & print variable stickers!');
    }
  }, [currentStepIndex, syncTabForStep, toast]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevIdx = currentStepIndex - 1;
      setCurrentStepIndex(prevIdx);
      syncTabForStep(TOUR_STEPS[prevIdx]);
    }
  }, [currentStepIndex, syncTabForStep]);

  const goToStep = useCallback((index: number) => {
    const validIndex = Math.max(0, Math.min(index, TOUR_STEPS.length - 1));
    setCurrentStepIndex(validIndex);
    syncTabForStep(TOUR_STEPS[validIndex]);
  }, [syncTabForStep]);

  const dismissWelcomePrompt = useCallback(() => {
    setShowWelcomePrompt(false);
    try {
      localStorage.setItem(LOCAL_STORAGE_WELCOME_DISMISSED, 'true');
    } catch {}
  }, []);

  // Keyboard navigation listener (ArrowRight / ArrowLeft / Escape)
  useEffect(() => {
    if (!isTourOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or textarea
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevStep();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        closeTour();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isTourOpen, nextStep, prevStep, closeTour]);

  // Execute step interactive action
  const runStepAction = useCallback(() => {
    if (!currentStep.actionType) return;

    switch (currentStep.actionType) {
      case 'load_sample_data':
        setCsvData({
          headers: ['SKU', 'ProductName', 'Price', 'Barcode', 'Category', 'QrLink'],
          rows: [
            { SKU: 'PROD-101', ProductName: 'Wireless Noise-Canceling Headphones', Price: '79.99', Barcode: '8901234567891', Category: 'Electronics', QrLink: 'https://store.example.com/p/101' },
            { SKU: 'PROD-102', ProductName: 'Ergonomic Memory Foam Cushion', Price: '34.50', Barcode: '8901234567907', Category: 'Office', QrLink: 'https://store.example.com/p/102' },
            { SKU: 'PROD-103', ProductName: 'Stainless Steel Thermal Tumbler', Price: '24.95', Barcode: '8901234567914', Category: 'Home', QrLink: 'https://store.example.com/p/103' },
            { SKU: 'PROD-104', ProductName: 'Smart Fitness Tracker Band', Price: '49.00', Barcode: '8901234567921', Category: 'Fitness', QrLink: 'https://store.example.com/p/104' },
            { SKU: 'PROD-105', ProductName: 'Braided Fast-Charging USB-C Cable', Price: '12.99', Barcode: '8901234567938', Category: 'Electronics', QrLink: 'https://store.example.com/p/105' },
            { SKU: 'PROD-106', ProductName: 'Organic Green Tea Blend 100g', Price: '18.50', Barcode: '8901234567945', Category: 'Grocery', QrLink: 'https://store.example.com/p/106' },
          ]
        });
        toast.success('Sample Data Loaded', 'Sample E-Commerce dataset loaded successfully!');
        break;

      case 'add_qr': {
        const hasQr = template.elements.some(e => e.type === 'qr');
        if (!hasQr) {
          const newEl: VdpElement = {
            id: `qr-${Date.now()}`,
            type: 'qr',
            x: 40,
            y: 30,
            width: 70,
            height: 70,
            content: 'https://store.example.com',
            columnName: 'QrLink',
            isStatic: false,
            color: '#000000',
            backgroundColor: '#ffffff'
          };
          setTemplate({
            ...template,
            elements: [...template.elements, newEl]
          });
          toast.info('Element Added', 'Added dynamic QR element to canvas!');
        } else {
          toast.info('Already Added', 'Dynamic QR element is already on canvas.');
        }
        break;
      }

      case 'open_3d':
        threeD.open3DModal();
        break;

      case 'auto_optimize': {
        const bleedPt = layout.bleed ?? 0;
        const itemWPt = (template.width || 300) + (bleedPt * 2);
        const itemHPt = (template.height || 300) + (bleedPt * 2);
        const marginTop = layout.marginTop ?? 14.17;
        const marginLeft = layout.marginLeft ?? 14.17;
        const marginBottom = layout.marginBottom ?? 14.17;
        const marginRight = layout.marginRight ?? 14.17;
        const gapX = layout.gapX ?? 5.67;
        const gapY = layout.gapY ?? 5.67;
        const sheetW = layout.sheetWidthPt || 935; // SRA3 default width
        const sheetH = layout.sheetHeightPt || 1369; // SRA3 default height

        const availW = Math.max(10, sheetW - marginLeft - marginRight);
        const availH = Math.max(10, sheetH - marginTop - marginBottom);

        const cols = Math.max(1, Math.floor((availW + gapX) / (itemWPt + gapX)));
        const rows = Math.max(1, Math.floor((availH + gapY) / (itemHPt + gapY)));

        setLayout(prev => ({
          ...prev,
          cols,
          rows
        }));
        toast.success('Grid Auto-Optimized', `Fitted ${cols} cols × ${rows} rows (${cols * rows} stickers/sheet)`);
        break;
      }

      case 'switch_tab':
        if (currentStepIndex === 3) {
          nextStep();
        }
        break;
    }
  }, [currentStep, template, layout, setCsvData, setTemplate, setLayout, threeD, toast, nextStep, currentStepIndex]);

  return (
    <TourContext.Provider
      value={{
        isTourOpen,
        currentStepIndex,
        totalSteps: TOUR_STEPS.length,
        currentStep,
        startTour,
        closeTour,
        nextStep,
        prevStep,
        goToStep,
        showWelcomePrompt,
        dismissWelcomePrompt,
        hasCompletedTour,
        runStepAction,
        hasAction: Boolean(currentStep.actionType && currentStep.actionLabel)
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
};
